/**
 * Movie Mayhem design tokens - the single source of truth for the Tamil Mass-Hero
 * Cinema Poster visual system.
 *
 * Consumed by:
 *   - `ThemeProvider.js`      -> passes the object to styled-components
 *   - `GlobalStyle.js`        -> flattens these into CSS custom properties on :root
 *                                so plain .css files can also read `var(--color-red-600)`
 *
 * Hard rules (enforced by review, not code):
 *   - Red is the CANVAS. Marketing surfaces use red-600..red-900, arena uses
 *     red-800..red-950 (15% darker, for legibility over data).
 *   - Ink fills panels / buttons / overlays only. Ink never fills a whole screen.
 *   - Amber is the SINGLE highlight color (one CTA per screen, active-character
 *     frame, HP mid-state, Tamil transliteration subtitle).
 *   - No purples. No India tricolor. No rainbow gradients.
 *   - Exactly two ambient gradients: homepage red radial, VICTORY amber spotlight.
 *   - Every red surface is covered by the shared grain overlay at a
 *     context-appropriate opacity (see `grain.opacity`).
 *   - Cut-corner is the dominant shape. Rounded pills are reserved for the
 *     countdown (peaceful state) and the players-online ticker only.
 */

export const color = {
    // Canvas + drama. Every top-level background is some value of red.
    red: {
        950: '#3A0A10', // deepest - vignette edges
        900: '#5A0D15', // arena canvas
        800: '#6B0E16', // arena panels
        700: '#8A1720', // homepage canvas mid
        600: '#A91D2A', // homepage canvas top
        500: '#C1272D', // key - HP danger, defeat banner
        400: '#E03131', // hover / critical accent
        300: '#EE5A52', // fade-out only
    },

    // Ink fills panels, buttons, overlays. Never a whole screen.
    ink: {
        950: '#08070A',
        900: '#0E0C10',
        800: '#16131A',
        700: '#22202A',
        600: '#2E2B38',
        500: '#4A4654',
        400: '#6F6B7A',
    },

    // Primary text color on red surfaces. Warm, not pure white.
    cream: {
        100: '#F6F1E4', // headlines, wordmark
        200: '#E8DFC8', // body
        300: '#BFB3A0', // muted, disabled, dividers
    },

    // Single highlight color. Use for ONE thing per screen.
    amber: {
        700: '#B06E10',
        600: '#E89420',
        500: '#FFB13C', // key - CTA, active frame, Tamil subtitle
        400: '#FFC76B', // hover
    },

    // Semantic aliases. Prefer these in component code over raw palette refs
    // so the intent is readable in the JSX.
    semantic: {
        success: '#67C97A',  // HP "good" tick only
        warning: '#FFB13C',  // = amber-500
        danger: '#C1272D',   // = red-500
    },
};

export const font = {
    family: {
        // Ultra-condensed hero / banner / giant numbers.
        display: "'Anton', 'Oswald', Impact, system-ui, sans-serif",
        // Structural display - section titles, move buttons, nav, stat labels.
        heading: "'Oswald', 'Anton', Impact, system-ui, sans-serif",
        // Body copy, input fields, tooltips, battle-log descriptions.
        body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        // Countdown, HP %, rating deltas, rank numbers.
        mono: "'JetBrains Mono', 'SF Mono', Menlo, Consolas, monospace",
        // Tamil transliteration - wordmark subtitle, per-character names,
        // section headers. Catamaran ships Latin + Tamil in one family.
        tamil: "'Catamaran', 'Noto Sans Tamil', 'Inter', sans-serif",
    },
    weight: {
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700,
    },
    // Scale tuned for mass-poster energy - 6xl is VICTORY/DEFEAT and the homepage
    // hero wordmark. 7xl is a reserved desktop-ultra variant.
    size: {
        '2xs': '10px',
        xs: '12px',
        sm: '14px',
        base: '16px',
        lg: '20px',
        xl: '24px',
        '2xl': '32px',
        '3xl': '48px',
        '4xl': '72px',
        '5xl': '112px',
        '6xl': '160px',
        '7xl': '220px',
    },
    tracking: {
        tight: '0.02em',  // Anton at display sizes (tight, poster-packed)
        normal: '0',       // Inter, Catamaran
        wide: '0.08em',   // Oswald at display sizes (title-card feel)
        wider: '0.12em',  // small caps, slash decorators
        widest: '0.18em', // overlines, eyebrows, button labels
    },
    lineHeight: {
        tight: 1.05,   // display
        snug: 1.2,     // headings
        normal: 1.5,   // body
        loose: 1.7,    // long-form
    },
};

// The dominant shape language is the 10-24px diagonal cut-corner. Radii are
// kept around for the two explicit exceptions (countdown pill, players-online
// ticker) and for subtle softening on input fields.
export const radius = {
    none: '0',
    xs: '4px',
    sm: '8px',
    md: '12px',
    pill: '999px',
};

// clip-path polygon strings producing the bottom-right diagonal cut.
// Use via `clip-path: ${clip.button}` inside styled-components.
export const clip = {
    button: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)',
    card:   'polygon(0 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%)',
    hero:   'polygon(0 0, 100% 0, 100% calc(100% - 24px), calc(100% - 24px) 100%, 0 100%)',
};

export const shadow = {
    none: 'none',
    // Four elevation levels - warm-tinted black, not the blueish default.
    sm: '0 1px 2px rgba(8, 7, 10, 0.25)',
    md: '0 4px 12px rgba(8, 7, 10, 0.35)',
    lg: '0 8px 28px rgba(8, 7, 10, 0.45)',
    xl: '0 20px 60px rgba(8, 7, 10, 0.55)',
    // The only two glow shadows. Used sparingly - CTA hover + active character.
    glowAmber: '0 0 24px rgba(255, 177, 60, 0.45), 0 0 2px rgba(255, 177, 60, 0.6)',
    glowRed:   '0 0 24px rgba(224, 49, 49, 0.45)',
};

// Spacing scale (4px base) for layout gaps, padding, margins.
export const spacing = {
    0: '0',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    8: '32px',
    10: '40px',
    12: '48px',
    16: '64px',
    20: '80px',
    24: '96px',
};

// Grain / halftone opacity bands. One shared SVG texture, three contexts.
// Applied globally via the `body::before` overlay; individual components can
// override locally via a --grain-opacity CSS variable on a parent.
export const grain = {
    opacity: {
        marketing: 0.18, // home, roster, leaderboard hero, end-match banner
        arena: 0.12,     // battle arena canvas
        panel: 0.06,     // team columns, battle log - crisper for data density
        none: 0,         // modal content that needs perfectly crisp text
    },
    halftoneOpacity: 0.08, // homepage only, behind the wordmark
    // URL paths (resolved at runtime against the public folder)
    grainUrl: '/brand/grain.svg',
    halftoneUrl: '/brand/halftone.svg',
};

// Motion durations / easings. Framer-motion and CSS both read these.
export const motion = {
    duration: {
        fast: 120,   // micro - hover color shifts, dot pulses (ms)
        base: 180,   // standard - enter/exit, state changes
        slow: 220,   // modal reveals, banner scale-up
        slower: 320, // side drawers, panel slides
        grain: 8000, // infinite grain drift
    },
    ease: {
        out: 'cubic-bezier(0.16, 1, 0.3, 1)',
        inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
};

// Named layout breakpoints - used sparingly, mobile-first
export const breakpoint = {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
};

// The flat theme object consumed by styled-components.
export const theme = {
    color,
    font,
    radius,
    clip,
    shadow,
    spacing,
    grain,
    motion,
    breakpoint,
};

export default theme;
